import type { RcSectionStatus } from './types';
import type { LineItemsSummary } from './types-line-item';

// Section status for the left-nav tree node (mirror of DO's
// deriveInterestSectionStatus): complete once every active line is confirmed,
// in_progress once any line is confirmed/edited, else not_started. Tiles count
// only active (non-removed) lines, so `total` is the active count.
export const deriveLineItemSectionStatus = (
  summary?: LineItemsSummary | null
): RcSectionStatus => {
  if (!summary || summary.total === 0) return 'not_started';
  if (summary.confirmed >= summary.total) return 'complete';
  if (summary.confirmed > 0 || summary.edited > 0) return 'in_progress';
  return 'not_started';
};

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// Parse the leading YYYY-MM of an iso8601 production month WITHOUT constructing a
// Date (a first-of-month UTC timestamp can render as the previous month in a
// behind-UTC locale).
const parseMonth = (
  iso: string | null
): { year: number; month: number } | null => {
  if (!iso) return null;
  const match = iso.match(/^(\d{4})-(\d{2})/);
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return { year: Number(match[1]), month };
};

// Format a well group's production period from its min/max production months:
// "Aug 2023" (single), "Aug – Oct 2023" (same year), "Aug 2023 – Jan 2024"
// (spanning years), "—" when neither bound is present.
export const formatProductionPeriod = (
  startISO: string | null,
  endISO: string | null
): string => {
  const start = parseMonth(startISO);
  const end = parseMonth(endISO);
  const first = start ?? end;
  const last = end ?? start;
  if (!first || !last) return '—';

  const withYear = (p: { year: number; month: number }) =>
    `${MONTHS[p.month - 1]} ${p.year}`;
  if (first.year === last.year && first.month === last.month)
    return withYear(first);
  if (first.year === last.year)
    return `${MONTHS[first.month - 1]} – ${withYear(last)}`;
  return `${withYear(first)} – ${withYear(last)}`;
};
